import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react'
import { PollOutlined, Delete } from '@mui/icons-material'
import { TableCell, TableRow, Checkbox, Tooltip, Fab, CircularProgress } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { motion } from 'framer-motion'
import { Workflow as HeaderIcon } from 'lucide-react'
import { object } from 'prop-types'
import { useDispatch } from 'react-redux'
import { Paper, CvsDrawer } from '@components'
import { AdminHeader } from '@components/layout/admin-header'
import { accountContext, modalContext } from '@contexts'
import { colors } from '@global/constants'
import { api } from 'api'
import { showSnackbar, getCellContent } from 'functions'
import { EditAccount } from './edit-account'
import { CvsTable } from '../../../components/table/cvs-table'
import { EnhancedTableToolbar } from '../../../components/table/enhanced-table-toolbar'
import * as SnackbarActions from '../../../global/snackbar/snackbar.actions'
import { t } from '@lingui/core/macro'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    height: '100%',
  },
  paper: {
    width: '100%',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 0,
  },
  table: {
    width: '100%',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  textField: {
    width: 200,
  },
}))

export const Accounts = ({ location, history }) => {
  const classes = useStyles()
  const ModalContext = useContext(modalContext)
  const { byId, fetchAccounts, updateConnectedAccounts } = useContext(accountContext)
  const [loaded, setLoaded] = useState(false)
  const [selected, setSelected] = useState({})
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [visible, setVisible] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const columns = [
    { label: t`Name`, width: '25%', lookup: 'name' },
    { label: t`Type`, width: '25%', lookup: 'type' },
    { label: t`Description`, width: '35%', lookup: 'description' },
    { label: t`Date Updated`, width: '15%', lookup: 'updated_at' },
  ]

  const accounts = useMemo(() => Object.keys(byId).map(id => byId[id]), [byId])

  const displayAccounts = useMemo(() => {
    if (!searchTerm) {
      return accounts
    }

    return accounts.filter(group => group?.name.toLowerCase().includes(searchTerm.toLowerCase()))
  }, [accounts, searchTerm])

  const dispatch = useDispatch()

  useEffect(() => {
    if (
      location.search.includes('oauth_success') &&
      location.search.includes('oauth_success=true')
    ) {
      dispatch(
        showSnackbar({
          message: t`Connected Account saved successfully`,
          type: 'SUCCESS',
        }),
      )
      history.replace('/admin/accounts', { search: {} })
      setVisible(false)
    } else {
      const queryParams = new URLSearchParams(location.search)

      location.search.includes('oauth_success') &&
        dispatch(
          showSnackbar({
            message:
              queryParams.get('message') ??
              t`Connected Account failed to save. The given data was invalid.`,
            type: 'ERROR',
          }),
        )
    }
  }, [])

  useEffect(() => {
    setLoaded(false)
    fetchAccounts().then(() => setLoaded(true))
  }, [fetchAccounts])

  const filterChanged = useCallback(setSearchTerm, [])

  useEffect(() => {
    if (selectedAccount) {
      setVisible(true)
    }
  }, [selectedAccount])

  useEffect(() => {
    if (!visible) {
      setSelectedAccount(null)
    }
  }, [visible])

  const onReset = () => {}

  const editItems = () => {}

  const addItem = () => {
    setSelectedAccount(null)
    setVisible(true)
  }

  const deleteItems = payload => {
    api
      .post('/api/v1/account/connector/connectors/bulk/delete', {
        connector_ids: Object.keys(payload).filter(p => payload[p]),
      })
      .then(() => {
        const filteredConnectedAccounts = accounts.filter(ca => !payload[ca.id])
        updateConnectedAccounts(filteredConnectedAccounts)
        setSelected({})
        dispatch(
          SnackbarActions.showSnackbar({
            message: t`Account deleted successfully`,
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
      })
      .catch(response => {
        dispatch(
          SnackbarActions.showSnackbar({
            message: response?.data?.message ? response.data.message : t`Account failed to delete`,
            type: 'ERROR',
            duration: 5000,
          }),
        )
      })
  }

  const deleteItemsModal = () => {
    ModalContext.displayModal({
      title: t`Are you sure you want to delete these Connected Accounts?`,
      description: t`Connected Accounts are an essential part of the Claravine experience and many parts of the platform depend on them. Please be sure this is what you'd like to do as this action can't be undone.`,
      confirm: () => {
        return () => {
          deleteItems(selected)
        }
      },
    })
  }

  const numSelected = Object.values(selected).filter(val => !!val).length

  const name = byId[selectedAccount]?.name
  const accountExists = selectedAccount && !!name

  return (
    <motion.div
      className={classes.root}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      style={{ overflow: 'hidden' }}
    >
      <AdminHeader>
        <AdminHeader.PageIcon>
          <HeaderIcon color="hsla(194, 47%, 20%, 1)" size={28} />
        </AdminHeader.PageIcon>
        <div className="ml-4">
          <AdminHeader.Title>{t`Accounts`}</AdminHeader.Title>
          <AdminHeader.Subtitle>
            {t`Connected Accounts unlock the full power of the Claravine platform by enabling integrations across the marketing ecosystem`}
          </AdminHeader.Subtitle>
        </div>
      </AdminHeader>
      <Paper elevation={2} className={classes.paper}>
        {loaded ? (
          <div className={classes.table}>
            <EnhancedTableToolbar
              numSelected={numSelected}
              term={searchTerm}
              filterChanged={filterChanged}
              addItem={addItem}
              editItems={editItems}
              deleteItems={deleteItems}
              noAdd={false}
              onReset={onReset}
              blockMultipleEdit={false}
            >
              <Tooltip
                title={t`Delete`}
                style={{ backgroundColor: colors.red.light, color: 'white' }}
              >
                <Fab
                  color="inherit"
                  size="small"
                  aria-label="Add"
                  className={classes.fab}
                  onClick={deleteItemsModal}
                >
                  <Delete />
                </Fab>
              </Tooltip>
            </EnhancedTableToolbar>
            <div style={{ height: 'calc(100% - 64px)' }}>
              <CvsTable
                columns={columns}
                rows={displayAccounts}
                orderBy="updated_at"
                sortOrder="desc"
                keyField="id"
                noAdd={false}
                addItem={addItem}
                noPagination
                style={{ maxWidth: '100%' }}
                multiSelect
                emptyStateMessage={{
                  icon: (
                    <Fab
                      style={{
                        height: '65px',
                        width: '65px',
                        color: colors.gray.light,
                        cursor: 'auto',
                      }}
                      disabled
                    >
                      <PollOutlined style={{ fontSize: '34px', color: colors.blue.smokey }} />
                    </Fab>
                  ),
                  title: t`No Accounts Found`,
                  description: t`Try a different search or add a new connected account`,
                }}
                selectAll={val => {
                  setSelected(
                    displayAccounts.reduce(
                      (memo, item) => ({
                        ...memo,
                        [item.id]: val,
                      }),
                      {},
                    ),
                  )
                }}
              >
                {({ row }) => {
                  return (
                    <TableRow
                      hover
                      onClick={() => {
                        numSelected
                          ? setSelected({
                              ...selected,
                              [row.id]: !selected[row.id],
                            })
                          : setSelectedAccount(row.id)
                      }}
                      tabIndex={-1}
                      key={row.id}
                      style={{ cursor: 'pointer', maxWidth: '100%', overflow: 'hidden' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          onClick={e => {
                            e.stopPropagation()
                            setSelected({
                              ...selected,
                              [row.id]: !selected[row.id],
                            })
                          }}
                          checked={selected[row.id]}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.name}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.type}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.description}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {getCellContent('date', row.updated_at)}
                      </TableCell>
                    </TableRow>
                  )
                }}
              </CvsTable>
            </div>
          </div>
        ) : (
          <div style={{ padding: 24, display: 'flex', width: '100%', justifyContent: 'center' }}>
            <CircularProgress size={15} color="primary" />
          </div>
        )}
      </Paper>
      <CvsDrawer
        containerStyles={{
          width: 600,
        }}
        visible={Boolean(visible)}
        onClose={() => {
          setVisible(false)
        }}
        title={accountExists ? t`Edit ${name}` : t`Create Connected Account`}
      >
        <EditAccount
          accountId={selectedAccount}
          onClose={() => {
            setVisible(false)
          }}
          newConnection={!accountExists}
        />
      </CvsDrawer>
    </motion.div>
  )
}

Accounts.propTypes = {
  location: object.isRequired,
  history: object.isRequired,
}
